import { useLiveStreamAppContext } from "../ContextStore/Store";

const PermissionsFetch = () => {
  const {
    camera,
    setCamera,
    setCameras,
    setMicrophones,
    setSpeakers,
    appInsightTrack,
    __merchantId,
    __userId,
    setPermissionError,
    setCameraPermission,
    setMicrophonePermission
  } = useLiveStreamAppContext();

  // Safari < 16 has no navigator.permissions, so Permission.jsx can never report "granted" -
  // a successful getUserMedia is then the only proof of access
  const isPermissionsApiUnavailable = !navigator.permissions?.query;

  async function fetchDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      // Filter video devices, excluding those with 'Default' or 'Communications' in the label
      const videoDevices = devices.filter(
        (device) =>
          device.kind === "videoinput" &&
          !device.label.toLowerCase().includes("default") &&
          !device.label.toLowerCase().includes("communications")
      );
      !camera && setCamera(videoDevices[0]?.deviceId);
      // Filter audio input devices, excluding 'Default' and 'Communications'
      const audioInputDevices = devices.filter(
        (device) =>
          device.kind === "audioinput" &&
          !device.label.toLowerCase().includes("default") &&
          !device.label.toLowerCase().includes("communications")
      );
      // Filter audio output devices, excluding 'Default' and 'Communications'
      const audioOutputDevices = devices.filter(
        (device) =>
          device.kind === "audiooutput" &&
          !device.label.toLowerCase().includes("default") &&
          !device.label.toLowerCase().includes("communications")
      );
      setCameras(videoDevices);
      setMicrophones(audioInputDevices);
      setSpeakers(audioOutputDevices);
    } catch (error) {
      console.error("Error accessing media devices:", error);
      appInsightTrack(__merchantId, __userId, "Error accessing media devices", "", error);
    }
  }

  // Returns true when the Permissions API confirms the permission is already granted,
  // so we can avoid calling getUserMedia while a call is in progress
  async function hasGrantedPermission(name) {
    try {
      const status = await navigator.permissions.query({ name });
      return status.state === "granted";
    } catch (error) {
      // Permissions API unavailable for this descriptor (e.g. older Firefox) - fall back to getUserMedia
      return false;
    }
  }

  async function requestAndRelease(constraints) {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    // Stop the tracks immediately - keeping them alive holds the camera/mic and
    // steals device access from the ACS call, dropping the live audio/video stream
    stream.getTracks().forEach((track) => track.stop());
  }

  // Check if camera and microphone permissions are granted
  async function checkPermissions() {
    let cameraGranted = await hasGrantedPermission("camera");
    let microphoneGranted = await hasGrantedPermission("microphone");

    if (!cameraGranted) {
      try {
        await requestAndRelease({ video: true });
        cameraGranted = true;
        isPermissionsApiUnavailable && setCameraPermission("granted");
      } catch (error) {
        appInsightTrack(__merchantId, __userId, "Camera permission denied", "", error);
      }
    }

    if (!microphoneGranted) {
      try {
        await requestAndRelease({ audio: true });
        microphoneGranted = true;
        isPermissionsApiUnavailable && setMicrophonePermission("granted");
      } catch (error) {
        appInsightTrack(__merchantId, __userId, "Microphone permission denied", "", error);
      }
    }

    if (cameraGranted || microphoneGranted) {
      fetchDevices();
    } else {
      // If neither permission is granted, show error popup
      setPermissionError(true);
    }
  }
  return { checkPermissions }
}

export default PermissionsFetch;